/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useCallback, useMemo } from 'react';
import { DynamicFormElement } from './DynamicFormElement';
import { IDynamicFormProps, IFormElementConfig, IFormData } from '../../../models/softwaredetails';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Banner 
} from '@forge/common';
import './DynamicForm.scss';

const DynamicForm: React.FC<IDynamicFormProps> = ({
  title,
  subTitle,
  config,
  onSubmit,
  onCancel,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  showWarningMessage = false,
  notificationProps
}) => {

  const [formData, setFormData] = useState<IFormData>(() => {
    const initialData: IFormData = {};
    config.forEach(field => {
      initialData[field.id] = field.value || '';
    });
    return initialData;
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [showNotification, setShowNotification] = useState(true);

  const memoizedConfig = useMemo(() => config, [JSON.stringify(config.map(c => c.id))]);

  const handleFieldChange = useCallback((fieldId: string, value: any) => {
    setFormData(prev => {
    // Only update if value actually changed
    if (prev[fieldId] === value) return prev;
    return { ...prev, [fieldId]: value };
    });

    // Clear error when user types
    setErrors(prev => {
    if (!prev[fieldId]) return prev;
    return { ...prev, [fieldId]: '' };
    });
  }, []);

  const handleFieldBlur = useCallback((fieldId: string) => {
    setTouched(prev => ({ ...prev, [fieldId]: true }));
    
    // Validate on blur
    const field = memoizedConfig.find(f => f.id === fieldId);
    if (field && field.validators) {
    const error = validateField(fieldId, formData[fieldId]);
    setErrors(prev => ({ ...prev, [fieldId]: error }));
    }
  }, [formData, memoizedConfig]);

  // Validate a single field
  const validateField = useCallback((fieldId: string, value: any): string => {
    const field = memoizedConfig.find(f => f.id === fieldId);
    if (!field || !field.validators) return '';

    const strValue = String(value || '');

        for (const validator of field.validators) {
        switch (validator.type) {
            case 'required':
            if (!strValue.trim()) {
                return validator.message || 'This field is required';
            }
            break;

            case 'minLength':
            if (strValue.length < (validator.value || 0)) {
                return validator.message || `Minimum ${validator.value} characters required`;
            }
            break;

            case 'pattern':
            if (validator.value && !(validator.value as RegExp).test(strValue)) {
                return validator.message || 'Invalid format';
            }
            break;

            case 'match':
            if (validator.matchField && formData[validator.matchField] !== strValue) {
                return validator.message || 'Fields do not match';
            }
            break;

            case 'custom':
            if (validator.validate) {
                const customError = validator.validate(strValue, formData);
                if (customError) return customError;
            }
            break;
        }
        }
    return '';
  }, [memoizedConfig, formData]);

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    memoizedConfig.forEach(field => {
      const error = validateField(field.id, formData[field.id]);
      if (error) {
        newErrors[field.id] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [memoizedConfig, formData, validateField]);

  // Check if form is valid (for button disabled state)
  const isFormValid = useMemo(() => {
    return memoizedConfig.every(field => {
      if (!field.validators || field.validators.length === 0) return true;
      const error = validateField(field.id, formData[field.id]);
      return !error;
    });
  }, [memoizedConfig, formData, validateField]);

    const handleSubmit = () => {
    const allTouched: { [key: string]: boolean } = {};
    config.forEach(field => {
        allTouched[field.id] = true;
    });
    setTouched(allTouched);

    // Validate all fields
    const newErrors: { [key: string]: string } = {};
    let isValid = true;

    config.forEach(field => {
        const error = validateField(field.id, formData[field.id]);
        if (error) {
        newErrors[field.id] = error;
        isValid = false;
        }
    });

    setErrors(newErrors);

    // Only submit if valid
    if (isValid) {
        onSubmit(formData);
    }
    };

  const getBannerType = (severity: string): 'info' | 'warning' | 'success' | 'critical' => {
    switch (severity) {
      case 'information':
        return 'info';
      case 'error':
        return 'critical';
      case 'important':
        return 'warning';
      default:
        return severity as 'info' | 'warning' | 'success' | 'critical';
    }
  };

  return (
    <Card className="dynamic-form">
      {title && (
        <CardHeader className="dynamic-form-header">
          {title}
        </CardHeader>
      )}

      <CardContent className="dynamic-form-content">
        {subTitle && (
          <div className="dynamic-form-sub-header">{subTitle}</div>
        )}

        {showWarningMessage && notificationProps && showNotification && (
          <div className="dynamic-form-notification">
            <Banner
              variant="inline"
              type={getBannerType(notificationProps.severity)}
              title={notificationProps.title}
              description={notificationProps.detailsText}
              canClose={true}
              onClose={() => setShowNotification(false)}
            />
          </div>
        )}

        <div className="dynamic-form-fields">
          {memoizedConfig.map((field) => (
            <DynamicFormElement
              key={field.id}
              {...field}
              value={formData[field.id] || ''}
              error={touched[field.id] ? errors[field.id] : ''}
              onChange={(value) => handleFieldChange(field.id, value)}
              onBlur={() => handleFieldBlur(field.id)}
              touched={touched[field.id]}
            />
          ))}
        </div>
      </CardContent>

      <div className="dynamic-form-footer">
        <div className="footer-buttons">
          {onCancel && (
            <Button
              variant="secondary"
              onClick={onCancel}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isFormValid}
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default DynamicForm;
